/**
 * usePresets Hook
 *
 * Custom hook for managing filter presets data fetching and state.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { Preset, PresetCreate, PresetUpdate } from '../types/presets';

interface UsePresetsReturn {
  presets: Preset[];
  systemPresets: Preset[];
  userPresets: Preset[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createPreset: (preset: PresetCreate) => Promise<Preset | null>;
  updatePreset: (presetId: number, updates: PresetUpdate) => Promise<Preset | null>;
  deletePreset: (presetId: number) => Promise<boolean>;
}

const PRESETS_CHANGED = 'babylon:presets-changed';

export function usePresets(): UsePresetsReturn {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all presets
  const fetchPresets = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getPresets();
      if (response.status === 'success') {
        setPresets(response.presets);
      } else {
        setError('Failed to fetch presets');
      }
    } catch (err) {
      console.error('Error fetching presets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch presets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch, and refetch whenever ANY usePresets instance changes the
  // list. The preset menu and the save dialog each call this hook, so without
  // the broadcast a new preset stayed invisible in the menu until reload.
  useEffect(() => {
    fetchPresets();
    window.addEventListener(PRESETS_CHANGED, fetchPresets);
    return () => window.removeEventListener(PRESETS_CHANGED, fetchPresets);
  }, [fetchPresets]);

  // Split presets into system and user
  // Note: the 4 legacy system presets were removed in migration a7c91f4b5d2e.
  // systemPresets is kept on the return shape for backward compatibility but
  // should always be empty in practice — the UI no longer renders it.
  const systemPresets = presets.filter(p => p.is_system);
  const userPresets = presets.filter(p => !p.is_system);

  // Create a new preset
  const createPreset = useCallback(async (preset: PresetCreate): Promise<Preset | null> => {
    try {
      const newPreset = await apiService.createPreset(preset);
      // Refetch here and in every other instance (menu, dialogs)
      window.dispatchEvent(new Event(PRESETS_CHANGED));
      await fetchPresets();
      return newPreset as Preset;
    } catch (err) {
      console.error('Error creating preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to create preset');
      return null;
    }
  }, [fetchPresets]);

  // Update a preset
  const updatePreset = useCallback(async (presetId: number, updates: PresetUpdate): Promise<Preset | null> => {
    try {
      const updatedPreset = await apiService.updatePreset(presetId, updates);
      // Refetch here and in every other instance (menu, dialogs)
      window.dispatchEvent(new Event(PRESETS_CHANGED));
      await fetchPresets();
      return updatedPreset as Preset;
    } catch (err) {
      console.error('Error updating preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to update preset');
      return null;
    }
  }, [fetchPresets]);

  // Delete a preset
  const deletePreset = useCallback(async (presetId: number): Promise<boolean> => {
    try {
      await apiService.deletePreset(presetId);
      // Refetch here and in every other instance (menu, dialogs)
      window.dispatchEvent(new Event(PRESETS_CHANGED));
      await fetchPresets();
      return true;
    } catch (err) {
      console.error('Error deleting preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete preset');
      return false;
    }
  }, [fetchPresets]);

  return {
    presets,
    systemPresets,
    userPresets,
    isLoading,
    error,
    refetch: fetchPresets,
    createPreset,
    updatePreset,
    deletePreset,
  };
}
